import React, { useState, FC, useEffect, useCallback } from 'react';
import ModifiedBy from './AnnotationModifiedBy';
import { FindingTypeDetails, FindingTypeRuleDetails } from '../../../AnnotationStandardForm/AnnotationStandardForm.slice';
import {
  updateAnnotation,
  setQAReviewPassed,
  createNewAnnotation,
  getFindingTypeProperties,
  canCloseAssessment,
  resetState,
  getAnnotationWarrantyCaseStatus
} from '../AnnotationForm.slice';
import { CreateAnnotationProperty } from '../AnnotationForm.types';
import AnnotationFormFooter from './AnnotationFormFooter';
import { connect } from 'react-redux';
import { RootState } from '../../../../../rootReducer';
import { AppDispatch } from '../../../../../store';
import { ASSESSMENT_STATUSES } from '../../annotation.types';
import { motion } from 'framer-motion';
import { fadein } from '../../../../Shared/Motion';
import { UseHaveAnyPermissions } from '../../../../../Auth/UseHaveAnyPermissions';
import { Permissions } from '../../../../../shared/models/permissions.enum';
import Assessment from '../Assessment/Assessment';
import AnnotationForm from './AnnotationForm';
import { isAnnotationFormValid, ANNOTATION_TAB_KEY } from '../Helpers';
import { useKeyPress } from '../../../../Shared/UseKeyPress';
import { isAssessmentCompleted, isStringNullOrEmpty } from '../Utils';

export const UNKNOWN_FINDING_TYPE_NAME = 'Unknown';

type Props = {
  selectedTabKey: string;
  cancelAnnotationCreation: () => void;
};

type StoreProps = ReturnType<typeof mapStateToProps> & ReturnType<typeof mapDispatchToProps>;

const AnnotationTab: FC<Props & StoreProps> = ({
  annotationForEdit,
  annotationStandard,
  selectedProperties,
  localization,
  localizationEditable,
  distanceToRoot,
  selectedTabKey,
  canSaveAssessment,
  submittingForm,
  assessment,
  selectedSequenceInfo,
  onGetFindingTypeProperties,
  onCreateAnnotation,
  onUpdateAnnotation,
  cancelAnnotationCreation,
  closeAssessment,
  onResetState,
  loadWarrantyCaseStatus,
  onSetQAReviewPassed
}) => {
  const [selectedFindingType, setSelectedFindingType] = useState<FindingTypeDetails | null>(null);
  const [properties, setProperties] = useState<CreateAnnotationProperty[][]>([]);
  const [selectedRules, setSelectedRules] = useState<FindingTypeRuleDetails[]>([]);
  const [findingTypeChange, setFindingTypeChange] = useState<boolean>(false);
  const [showAssessment, setShowAssessment] = useState(false);
  const [scrollToBottom, setScrollToBottom] = useState(false);
  const [description, setDescription] = useState('');

  const canWorkWithAssessment = UseHaveAnyPermissions([Permissions.AnnotationManager_RequestAssessment]);

  const onChange = useCallback(
    (id: string) => {
      setFindingTypeChange(true);
      const selectedType = annotationStandard!.findingTypes.find(x => x.id === id)!;
      setSelectedFindingType(selectedType);
      setSelectedRules(selectedType.rules);
      onGetFindingTypeProperties(selectedType.id!, selectedType.findingTypeTemplate!.id);
    },
    [annotationStandard, onGetFindingTypeProperties]
  );

  const onSave = useCallback(() => {
    const saveFunction = annotationForEdit ? onUpdateAnnotation : onCreateAnnotation;

    saveFunction(selectedFindingType!.findingTypeTemplate!.name, properties.flat());
  }, [annotationForEdit, onCreateAnnotation, onUpdateAnnotation, properties, selectedFindingType]);

  const askForAssessment = useCallback(() => {
    setShowAssessment(true);
    setScrollToBottom(true);
  }, []);

  useEffect(() => {
    if (annotationStandard && annotationForEdit) {
      const selectedType = annotationStandard.findingTypes.find(x => x.id === annotationForEdit.findingTypeId);
      setSelectedFindingType(selectedType!);
      setSelectedRules(selectedType!.rules);
      onGetFindingTypeProperties(selectedType!.id!, selectedType!.findingTypeTemplate!.id);

      if (annotationForEdit.newStatus && ASSESSMENT_STATUSES.includes(annotationForEdit.newStatus)) {
        setShowAssessment(true);
      }
    }
  }, [annotationForEdit, annotationStandard, onGetFindingTypeProperties]);

  useEffect(() => {
    if (canSaveAssessment && !showAssessment) {
      closeAssessment();
    }
  }, [canSaveAssessment, closeAssessment, showAssessment]);

  useEffect(() => {
    return () => {
      setShowAssessment(false);
      onResetState();
    };
  }, [onResetState]);

  useEffect(() => {
    loadWarrantyCaseStatus();
  }, []);

  const showModifiedBy = annotationForEdit && !isStringNullOrEmpty(annotationForEdit.user) && !isStringNullOrEmpty(annotationForEdit.dateCreated);
  const showAssessmentBlock = annotationForEdit && !!canWorkWithAssessment && showAssessment;
  const assessmentCompleted = isAssessmentCompleted(assessment);
  let saveDisabled = !selectedSequenceInfo || !selectedFindingType || assessmentCompleted || !isAnnotationFormValid(properties);
  const showQAReviewButton = annotationForEdit != null && annotationForEdit.showQAReviewPassedOption;

  useKeyPress('Enter', false, () => {
    if (document.activeElement instanceof HTMLInputElement || document.activeElement instanceof HTMLTextAreaElement) {
      return;
    }

    if (!saveDisabled && selectedTabKey === ANNOTATION_TAB_KEY) {
      onSave();
    }
  });

  return (
    <motion.div initial='hidden' animate='visible' variants={fadein}>
      {showModifiedBy && <ModifiedBy user={annotationForEdit!.user} date={annotationForEdit!.dateCreated} />}
      <AnnotationForm
        {...{
          annotationStandard,
          selectedFindingType,
          properties,
          selectedProperties,
          selectedRules,
          showAssessmentBlock,
          setProperties,
          onChange,
          distanceToRoot,
          annotationForEdit,
          localization,
          localizationEditable,
          findingTypeChange,
          setFindingTypeChange,
          assessmentCompleted
        }}
      />
      {showAssessmentBlock && (
        <Assessment
          {...{
            assessment,
            scrollToBottom,
            assessmentCompleted,
            canSaveAssessment,
            description,
            annotationId: annotationForEdit && annotationForEdit.id,
            setDescription
          }}
        />
      )}
      <AnnotationFormFooter
        saveDisabled={saveDisabled}
        onSubmit={onSave}
        onSetQAReviewPassed={onSetQAReviewPassed}
        selectedTabKey={selectedTabKey}
        cancelAnnotationCreation={cancelAnnotationCreation}
        showQAReviewButton={showQAReviewButton}
        showAskForAssessment={!showAssessment && !!annotationForEdit}
        askForAssessment={askForAssessment}
        isLoading={submittingForm}
      />
    </motion.div>
  );
};

const mapStateToProps = (state: RootState) => ({
  annotationStandard: state.annotation.annotationStandardDetails,
  annotationForEdit: state.annotationForm.selectedAnnotationForEdit,
  selectedProperties: state.annotationForm.selectedProperties,
  localization: state.annotation.findingLocalizationDto,
  localizationEditable: state.annotation.localizationEditable,
  distanceToRoot: state.annotationForm.initialDistanceToRoot,
  canSaveAssessment: state.annotationForm.canSaveAssessment,
  submittingForm: state.annotationForm.submittingForm,
  assessment: state.annotationForm.assessment,
  hasDefaultMaintenanceTrigger: state.annotation.annotationStandardDetails?.hasDefaultMaintenanceTrigger,
  responseLevels: state.maintenanceTriggerForm.responseLevels,
  selectedSequenceInfo: state.annotation.selectedSequenceInfo,
  isWarrantyCaseClosed: state.annotationForm.isWarrantyCaseClosed,
  selectedFindingTypeId: state.annotationForm.selectedFindingTypeId
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  onGetFindingTypeProperties: (id: string, templateId: string) => dispatch(getFindingTypeProperties(id, templateId)),
  onUpdateAnnotation: (name: string, properties: CreateAnnotationProperty[]) => dispatch(updateAnnotation(name, properties)),
  onSetQAReviewPassed: () => dispatch(setQAReviewPassed()),
  onCreateAnnotation: (name: string, properties: CreateAnnotationProperty[]) => dispatch(createNewAnnotation(name, properties)),
  closeAssessment: () => dispatch(canCloseAssessment()),
  onResetState: () => dispatch(resetState()),
  loadWarrantyCaseStatus: () => dispatch(getAnnotationWarrantyCaseStatus())
});

export default connect(mapStateToProps, mapDispatchToProps)(AnnotationTab);
